import { useMemo } from 'react';
import { AMP_SETTINGS } from '@ampsec/platform-client';
import { useTenantSettings } from './useTenantSettings';

export interface AgentManagementPreviewState {
    isLoading: boolean;
    isEnabled: boolean;
}

/**
 * Feature gate for the Agent Management preview (APD-1867).
 *
 * Reads the `securityhub.agentmanagement.preview.enabled` tenant setting via
 * `AMP_SETTINGS.tenant.AGENT_MANAGEMENT_PREVIEW_ENABLED`. Defaults to `false`
 * — the feature is hidden unless the setting is explicitly set to `true`.
 *
 * @example
 * const { isLoading, isEnabled } = useAgentManagementPreview();
 * if (isLoading) return <Spinner />;
 * if (!isEnabled) return <LockedState />;
 */
export const useAgentManagementPreview = (): AgentManagementPreviewState => {
    const { tenantSettings, getAmpSettingValue, settingsLoading } = useTenantSettings();

    return useMemo(() => {
        const isLoading = settingsLoading || !tenantSettings || Object.keys(tenantSettings).length === 0;

        if (isLoading) {
            return { isLoading: true, isEnabled: false };
        }

        const rawValue =
            getAmpSettingValue(AMP_SETTINGS.tenant.AGENT_MANAGEMENT_PREVIEW_ENABLED.id) ??
            AMP_SETTINGS.tenant.AGENT_MANAGEMENT_PREVIEW_ENABLED.defaultValue;

        const isEnabled = rawValue === true || rawValue === 'true';

        return { isLoading: false, isEnabled };
    }, [tenantSettings, settingsLoading, getAmpSettingValue]);
};
